import { useState } from 'react'
import {
  defaultSyslogSettingsFormState,
  SyslogSettingsFormState
} from '../syslog'
import { EditedValues, ServerSettingsFormSetterFactory } from '.'
import { EditShowData } from './server-settings-form-bulk-focus-manager'
import { ServerEnvironment } from '../server-environment'
import { LogLevel, StacktraceCaptureMode } from '../servers-grid/shared'
import { usePrevious } from '../../hooks/use-previous'
import { SAMPLING_OPTIONS } from './server-settings-form-single-view'

export interface ServerSettingsFormValueBulk {
  readonly logPath: string
  readonly environment?: ServerEnvironment
  readonly logLevel?: LogLevel
  readonly botProtection: boolean
  readonly stacktraceCaptureMode?: StacktraceCaptureMode
  readonly samplingProfile?: string
  readonly samplingEnabled: boolean
  readonly samplingFrequency: string
  readonly samplingBaseline: string
  readonly samplingWindow: string
  readonly syslogEnabled: boolean
  // TODO syslog enabled ?
  readonly syslog: SyslogSettingsFormState
}

export interface State {
  readonly formValue: ServerSettingsFormValueBulk
  readonly editedValues: EditedValues
  readonly showSyslogEdit: boolean
  readonly showSamplingEdit: boolean
  readonly showBotProtectionEdit: boolean
}

export function getBulkServerInitialState(): State {
  return {
    formValue: {
      environment: undefined,
      logPath: '',
      logLevel: undefined,
      botProtection: false,
      stacktraceCaptureMode: undefined,
      samplingProfile: SAMPLING_OPTIONS.PROD, // default profile if not set
      samplingEnabled: false,
      samplingBaseline: '5',
      samplingFrequency: '10',
      samplingWindow: '183',
      syslogEnabled: false,
      syslog: defaultSyslogSettingsFormState
    },
    showSyslogEdit: true,
    showSamplingEdit: true,
    showBotProtectionEdit: true,
    editedValues: {}
  }
}

export interface StateManagerChildrenProps {
  readonly state: State
  readonly isAllowedAssess: boolean
  readonly isAllowedProtect: boolean
  readonly isAllowedSyslog: boolean
  readonly showLogPath: boolean
  readonly disableBotProtectToggle: boolean
  readonly disableSamplingToggle: boolean
  readonly disableSyslogToggle: boolean
  readonly showSampling: boolean
  readonly showSyslog: boolean

  readonly reset: () => void
  readonly formSetter: ServerSettingsFormSetterFactory

  readonly isValueEdited: (key: keyof EditedValues) => boolean
  readonly wasEdited: boolean

  readonly disableShowBotProtectionEdit: () => void
  readonly disableShowSamplingEdit: () => void
  readonly disableShowSyslogEdit: () => void

  readonly editShowData: EditShowData
}

export const wasEdited = ({
  editedValues,
  showSyslogEdit,
  showSamplingEdit,
  showBotProtectionEdit
}: {
  readonly editedValues: State['editedValues']
  readonly showSyslogEdit: State['showSyslogEdit']
  readonly showSamplingEdit: State['showSamplingEdit']
  readonly showBotProtectionEdit: State['showBotProtectionEdit']
}) => {
  return (
    Object.keys(editedValues).some((k) => editedValues[k]) ||
    !(showBotProtectionEdit && showSamplingEdit && showSyslogEdit)
  )
}

export const isValueEdited = (
  editedValues: EditedValues,
  key: keyof EditedValues
) => typeof editedValues[key] !== 'undefined'

export interface StateManagerProps {
  readonly isFreemium: boolean
  readonly userIsProtect: boolean
  readonly children: (props: StateManagerChildrenProps) => any
}

export function ServerSettingsFormBulkStateManager({
  isFreemium,
  userIsProtect,
  children
}: StateManagerProps) {
  const [state, setState] = useState(getBulkServerInitialState())

  const prevState = usePrevious(state)

  const showSampling = state.formValue.samplingEnabled
  const showSyslog = state.formValue.syslogEnabled

  const disableShowBotProtectionEdit = () =>
    setState({
      ...state,
      showBotProtectionEdit: false
    })

  const disableShowSamplingEdit = () =>
    setState({
      ...state,
      showSamplingEdit: false
    })

  const disableShowSyslogEdit = () =>
    setState({
      ...state,
      showSyslogEdit: false
    })

  const formSetter: ServerSettingsFormSetterFactory = (key) => (value) => {
    return setState(({ formValue, editedValues }) => ({
      ...state,
      formValue: {
        ...formValue,
        [key]: value
      },
      editedValues: {
        ...editedValues,
        [key]: true
      }
    }))
  }

  const reset = () => setState(getBulkServerInitialState())

  return children({
    state,
    isValueEdited: (key) => isValueEdited(state.editedValues, key),
    wasEdited: wasEdited(state),
    reset,
    isAllowedAssess: true,
    isAllowedProtect: userIsProtect,
    isAllowedSyslog: userIsProtect && !isFreemium,
    showLogPath: false,
    disableBotProtectToggle: false,
    disableSamplingToggle: false,
    disableSyslogToggle: false,
    showSampling,
    showSyslog,
    formSetter,
    disableShowBotProtectionEdit,
    disableShowSamplingEdit,
    disableShowSyslogEdit,
    editShowData: {
      botProtection: {
        previous: prevState ? prevState.showBotProtectionEdit : undefined,
        current: state.showBotProtectionEdit
      },
      sampling: {
        previous: prevState ? prevState.showSamplingEdit : undefined,
        current: state.showSamplingEdit
      },
      syslog: {
        previous: prevState ? prevState.showSyslogEdit : undefined,
        current: state.showSyslogEdit
      }
    }
  })
}

export interface Props {
  readonly children: (props: {
    readonly setRefSamplingToggleInput: (ref: HTMLInputElement) => void
    readonly setRefBotProtectionToggle: (ref: HTMLInputElement) => void
    readonly setRefSyslogToggle: (ref: HTMLInputElement) => void
  }) => any
}
